// E14-t2 — THE HOME PAGE.   route: /
//
// Until this file there was NO home page: `/` fell through App.tsx's path router
// to `unknown`, and a fresh sign-in was sent straight to a KB-scoped surface — so
// the first thing after signing in had no account context around it at all.
//
// Contract: vault/Apps/Forge/Pages/Home.md — f1 ("Land signed-in people here and
//   show which organization they are looking at, with their role") and f2 ("Select
//   ONE organization at a time, switching explicitly, from GET /api/session
//   orgs"). The page's overview is the rule this file exists to hold:
//     ONE ORGANIZATION AT A TIME. "A person may belong to several … but showing
//     them all at once turns the landing page into a directory." So exactly one
//     org is rendered, its name and the viewer's ROLE IN IT are stated in words,
//     and switching is a control somebody has to operate.
//   And the constraint that decides how that control is drawn: "at the current
//   tier sign-up creates a personal org that admits no second member, so for most
//   people the switcher has exactly one entry AND MUST NOT LOOK BROKEN BECAUSE OF
//   IT." At n=1 the switcher is therefore present, disabled, and says WHY — a
//   control with a reason reads as deliberate; an empty dropdown reads as a bug.
//
// E14-t6 MOVED THAT CONTROL OUT OF THIS PAGE, and left everything around it.
// This page carried its own "Viewing organization" Selector, named that way only
// because AppShell's top bar already carried one called "Organization" and two
// controls sharing an accessible name is a coin toss for anybody navigating by
// name. Two controls for one decision is the actual defect, and an organization
// applies to EVERY page rather than to this one — so the global chrome keeps the
// control and this page keeps the CONTEXT: whose account this is, which
// organization, the role, and what platform/src/guards.ts grants that role. The
// n=1 sentence went with the control, onto the shell Selector's `disabledMessage`
// (AppShell.tsx SOLE_ORG_REASON), because the reason has to survive the move.
//
// WHICH ORG THIS PAGE IS ABOUT is therefore decided in two places, in this order:
// the chrome's own choice when there is one (`activeOrg`, handed down by
// src/App.tsx from the shell's Selector), else E14 q1's rule — last used, else
// the first. The page still hands its answer UP on mount, so the chrome opens on
// the same organization the page is showing rather than on whatever the session
// last named.
// Contract: vault/Backlog/E14 Customer Facing UI.md q1 (answered) — Home opens on
//   the LAST ORGANIZATION USED, remembered locally. First org when nothing is
//   remembered; first org again when the remembered one is no longer in the
//   session's list, and that value is FORGOTTEN rather than kept as a ghost.
// Contract: E14 t1 · Home f8 — the Astryx component layer. Every control here is
//   the design system's own; this file writes no CSS and imports no stylesheet.
// Contract: vault/Apps/Forge/Forge.md q1 (archived) — an org list is itself a
//   disclosure, so nothing on this page is rendered without a session.
//
// ROLE WORDING IS platform/src/guards.ts's, NOT this file's opinion:
// ROLE_CAPABILITIES gives a viewer ['read','capture'], 'command' to admin and
// maintainer, and 'govern' — invite-member, remove-member, set-guards — to admin
// alone. The sentences below say that and nothing more generous, because the
// Worker refuses regardless and a page that oversells a role teaches somebody to
// expect a door that will be shut.
//
// PRESENTATIONAL, like every other page here. The session is READ by the
// container (src/session.ts) and handed down; the one piece of local state this
// page owns — which org was last used — travels through an injected `OrgMemory`
// so the page can be rendered with no browser storage at all. The page never
// navigates: it hands the org it settled on UP, and App decides what that means
// for the shell's own context.
//
// E14-t3 FILLED THE SEAM this file used to mark: the knowledge-base list lives
// in src/pages/KnowledgeBases.tsx, under the organization it belongs to. It is a
// separate component because it is the only part of this page that touches the
// network — Home itself is still a pure function of the session and one
// remembered org, and that is worth keeping true.
import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@astryxdesign/core/Badge';
import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Heading } from '@astryxdesign/core/Heading';
import { Spinner } from '@astryxdesign/core/Spinner';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';

import { createKbsClient, type KbsClient } from '../api/kbs';
import { createReportsClient, type ReportsClient } from '../api/reports';
import { identityColor } from '../identity';
import type { OrgSummary, Role, SessionPayload } from '../shell/AppShell';
import { KnowledgeBases } from './KnowledgeBases';

/** Where "the last organization used" is remembered (E14 q1). The value is an org
 *  ID, not a name: ids survive a rename and names are what URLs carry. */
export const LAST_ORG_KEY = 'docujoint.forge.last-org';

/** The one bit of client state this page owns, behind an interface so the page
 *  can be rendered with no `window` at all. */
export interface OrgMemory {
  read(): string | null;
  write(orgId: string): void;
  forget(): void;
}

/** localStorage, or nothing. Every access is guarded: a browser with storage
 *  disabled (Safari private mode throws on the property itself) must degrade to
 *  "nothing is remembered", never to a page that fails to render. */
function localStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** The default memory. A module-level SINGLETON on purpose — a fresh object per
 *  render would change the identity of a hook dependency on every render. */
export const browserOrgMemory: OrgMemory = {
  read() {
    try {
      return localStore()?.getItem(LAST_ORG_KEY) ?? null;
    } catch {
      return null;
    }
  },
  write(orgId: string) {
    try {
      localStore()?.setItem(LAST_ORG_KEY, orgId);
    } catch {
      // Remembering is a convenience. Failing to remember is not an error worth
      // showing anybody, and it is certainly not worth throwing out of a render.
    }
  },
  forget() {
    try {
      localStore()?.removeItem(LAST_ORG_KEY);
    } catch {
      // as above
    }
  },
};

export interface OrgChoice {
  /** the ONE org this visit is about, or null when the session holds none */
  org: OrgSummary | null;
  /** a remembered id that names no org the session can reach any more. Kept so
   *  the page can SAY what happened instead of silently showing a different
   *  organization than the one somebody left open. */
  stale: string | null;
}

/**
 * E14 q1, as a pure function: last used, else the first, and a remembered value
 * that no longer names a reachable org falls back to the first AND is reported
 * as stale so the caller can drop it.
 *
 * Order is the control plane's (git-host/src/catalog.ts) — "the first" is the
 * first org GET /api/session listed, not an alphabetisation invented here.
 */
export function chooseOrg(orgs: readonly OrgSummary[], remembered: string | null): OrgChoice {
  if (orgs.length === 0) return { org: null, stale: null };
  if (remembered === null || remembered === '') return { org: orgs[0], stale: null };
  const found = orgs.find((candidate) => candidate.id === remembered);
  return found ? { org: found, stale: null } : { org: orgs[0], stale: remembered };
}

/** platform/src/guards.ts ROLE_CAPABILITIES, in a sentence. Nothing here promises
 *  a capability the guard does not grant. */
const ROLE_SENTENCE: Record<Role, string> = {
  admin:
    'As an admin you govern this organization: membership and guards are yours to change, on top of everything a maintainer can do.',
  maintainer:
    'As a maintainer you can push to this organization’s knowledge bases and run commands against them. Membership and guards belong to an admin.',
  viewer: 'As a viewer you can read this organization’s knowledge bases and capture from them.',
};

/**
 * The registry transport, built ONCE for the life of the module.
 *
 * A default rather than a required prop, for the same reason `browserOrgMemory`
 * above is one: the page has to be renderable on its own — a component test, a
 * story — without a container assembling three objects first, and a client
 * identity that changed per render would re-run the list read forever. The
 * default is late-bound (src/api/kbs.ts binds `fetch` inside a thunk), so it is
 * safe to construct here in an environment that has no fetch yet.
 */
const defaultKbsClient = createKbsClient();
const defaultReportsClient = createReportsClient();

/** The header button's plus — 16px, stroked, decorative (the label speaks). */
const plusSvg = (
  <svg
    aria-hidden="true"
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
  >
    <path d="M12 5.5v13" />
    <path d="M5.5 12h13" />
  </svg>
);

export interface HomePageProps {
  /** what the control plane answered for GET /api/session, or null for nobody */
  session: SessionPayload | null;
  /** App.tsx's `probed`: has that read ANSWERED yet? "Nobody is signed in" and
   *  "we have not asked yet" are the same null, and they are not the same page. */
  probed?: boolean;
  /** where the last-used org lives. Injected, like every other transport here. */
  memory?: OrgMemory;
  /** E14 t3: GET /api/kbs, GET /api/:org/:kb/usage and POST /api/kbs. Injected
   *  so the page can be driven with no network at all. */
  kbs?: KbsClient;
  /** E25-t4: the reports indicator's per-KB read, same injection rule. */
  reports?: ReportsClient;
  /** the org this page settled on, handed UP so the shell's own context agrees
   *  with what the reader is looking at. The page does not navigate. */
  onOrgSelected?: (orgName: string) => void;
  /** E14-t6 — THE ORGANIZATION THE CHROME IS ON, an org NAME, when the chrome
   *  has an answer. The switcher lives in AppShell now, so this is how operating
   *  it reaches the page under it. `null` (the state before this page has handed
   *  anything up) means "no answer yet", and E14 q1's rule decides instead. A
   *  name that is not in the session is ignored rather than obeyed — the page
   *  shows organizations this account is a member of and nothing else. */
  activeOrg?: string | null;
}

export function HomePage({
  session,
  probed = true,
  memory = browserOrgMemory,
  kbs = defaultKbsClient,
  reports = defaultReportsClient,
  onOrgSelected,
  activeOrg = null,
}: HomePageProps) {
  // Read ONCE per mount, then owned as state: after a fallback the page must keep
  // saying that the remembered org went away, and re-reading storage every render
  // would erase that sentence the moment the stale value is forgotten below.
  const [picked, setPicked] = useState<string | null>(() => memory.read());

  // 2026-08-07 design audit — the create button lives in the ORG HEADER, not on
  // the shelf. Each click is a bumped counter; KnowledgeBases owns the draft and
  // the role gate, so this page holds no create state of its own.
  const [createRequest, setCreateRequest] = useState(0);

  const orgs = useMemo(() => session?.orgs ?? [], [session]);
  const { org, stale } = chooseOrg(orgs, picked);

  // A remembered org the session cannot reach is not remembered any more. Doing
  // this in an effect rather than in `chooseOrg` keeps the choice pure.
  useEffect(() => {
    if (stale !== null) memory.forget();
  }, [stale, memory]);

  // Hand the choice up. Deliberately keyed on the NAME alone: if `onOrgSelected`
  // were a dependency, an inline arrow in the container would re-run this on
  // every render, and a container that stores what it receives would then loop.
  const selectedName = org?.name ?? null;
  useEffect(() => {
    if (selectedName !== null) onOrgSelected?.(selectedName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedName]);

  // ── THE CHROME'S SWITCHER, ARRIVING (E14-t6) ─────────────────────────────
  // The one organization control is AppShell's, so "somebody switched" reaches
  // this page as a changed `activeOrg` rather than as an event. Three guards,
  // each load-bearing:
  //   · no answer yet (null) leaves E14 q1's rule in charge, so a fresh mount
  //     still opens on the last organization used;
  //   · a name the session does not list is IGNORED — the chrome may carry the
  //     url's own org (App.tsx withRouteContext admits one), and this page must
  //     not draw an organization the control plane did not hand over;
  //   · a name that resolves to the org already on screen writes nothing, which
  //     is what keeps the handshake above (page hands up, container hands back)
  //     from re-remembering on every mount and erasing the stale-org banner.
  const currentId = org?.id ?? null;
  useEffect(() => {
    if (activeOrg === null) return;
    const chosen = orgs.find((candidate) => candidate.name === activeOrg);
    if (chosen === undefined || chosen.id === currentId) return;
    setPicked(chosen.id);
    // "Last one used" is only true if it is written down (E14 q1) — and this is
    // the one act that makes it true now that the control is elsewhere.
    memory.write(chosen.id);
  }, [activeOrg, orgs, currentId, memory]);

  // ── The identity read has not answered yet ────────────────────────────────
  // Not "you are signed out": we do not know yet, and saying the wrong one of
  // those two puts a sign-in prompt in front of somebody who is already signed in.
  if (session === null && !probed) {
    return (
      <Stack direction="vertical" gap={3} padding={6} hAlign="center" data-page="home" data-state="loading">
        <Spinner size="lg" />
        <Text type="body" color="secondary">
          Checking your session…
        </Text>
      </Stack>
    );
  }

  // ── Answered, and nobody is signed in ─────────────────────────────────────
  // App.tsx does not route here in that state (AppShell's own unauthenticated
  // face is a prompt with no chrome — Forge q1), so this is the page being
  // honest when it is rendered on its own rather than a screen anybody sees.
  if (session === null) {
    return (
      <Stack direction="vertical" gap={4} padding={4} data-page="home" data-state="anonymous">
        <Banner
          status="info"
          title="You are not signed in, so there is no organization to show."
          container="card"
        />
      </Stack>
    );
  }

  // ── Signed in, and a member of nothing ────────────────────────────────────
  // Sign-up grants a personal org (git-host/src/login.ts), so this is a
  // control-plane state rather than an ordinary one. It is named as such instead
  // of being drawn as an empty account, which would be a lie about why the page
  // is blank.
  if (org === null) {
    return (
      <Stack direction="vertical" gap={4} padding={4} data-page="home" data-state="no-orgs">
        <Banner
          status="warning"
          title="This account is not a member of any organization."
          description={`The forge answered for ${session.user.email} with an empty organization list. An account normally comes with one personal organization, so nothing can be shown here until a membership exists — an admin of an organization has to add you.`}
          container="card"
        />
      </Stack>
    );
  }

  return (
    <Stack direction="vertical" gap={4} padding={4} data-page="home" data-state="ready">
      {/* The remembered org is gone. Said out loud, because the alternative is
          quietly opening a DIFFERENT organization than the one somebody left. */}
      {stale === null ? null : (
        <Banner
          status="info"
          title="The organization you had open last is not one this account can reach any more, so this is the first one instead."
          container="card"
        />
      )}

      {/* WHOSE ORGANIZATION — the imported DocujointOverview header (2026-08-06):
          the identity dot, the org as the page's title, and the role sentence as
          the SUBTITLE. The old context card folded into this line; the region
          keeps its accessible name so it is still addressable as "Organization",
          and every fact it carried survives — who is signed in (the account
          menu), the org (here), the role and what it grants (the line below). */}
      {/* 2026-08-07 design audit: the primary action sits IN the header line,
          not on the shelf below — the org and its one act share the row. */}
      <Stack direction="horizontal" gap={4} vAlign="end" hAlign="between">
        <div role="region" aria-label="Organization" data-region="organization">
          <Stack direction="vertical" gap={1}>
            <Stack direction="horizontal" gap={2} vAlign="center">
              <span
                aria-hidden="true"
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: 999,
                  flex: '0 0 auto',
                  background: identityColor(org.name),
                }}
              />
              <Heading level={1} type="display-3">
                {org.name}
              </Heading>
              <Badge label={org.role} />
            </Stack>
            <Text type="supporting" color="secondary">
              Organization overview · every write refereed against its format spec. {ROLE_SENTENCE[org.role]}
            </Text>
          </Stack>
        </div>
        <Button
          label="New knowledge base"
          variant="primary"
          size="sm"
          icon={plusSvg}
          onClick={() => setCreateRequest((count) => count + 1)}
          data-testid="home-kb-create-open"
        />
      </Stack>

      {/*
        THE KNOWLEDGE BASES — E14 t3, directly under the organization they belong
        to. NOT keyed on the org: GET /api/kbs is scoped to the session's
        memberships rather than to one org (Tenancy f5), so switching
        organizations re-filters a list that is already in hand instead of
        emptying the region and asking again.

        STILL OPEN: opening one. Home f4 — a KB opens straight into its published
        dashboard — is E14 t4, which depends on this ticket.
      */}
      <KnowledgeBases
        org={org.name}
        role={org.role}
        client={kbs}
        reports={reports}
        createRequest={createRequest}
      />
    </Stack>
  );
}

export default HomePage;
